import { useSEO, BASE } from "@/lib/seo";
import { IMAGES } from "@/lib/images";
import ImageCredit from "@/components/site/ImageCredit";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ArticleShell, { H2, H3, P, SeeAlso } from "@/components/site/ArticleShell";
import { Reveal } from "@/components/site/Reveal";
import { RichText } from "@/components/site/RichText";
import { IlluFigure } from "@/components/site/IlluFigure";
import { HUB_CIRE_ILLUS } from "@/lib/illustrations";

const MATERIAUX = [
  {
    name: "Bronze",
    slug: "bronze",
    badge: "Fonte",
    text: "Alliage de cuivre et d'étain, le bronze reste le matériau noble de la sculpture. Coulé à la cire perdue, il autorise une précision extrême et une durée de vie de plusieurs millénaires. Sa patine — verte, brune, noire ou dorée — fait partie intégrante de l'œuvre.",
    points: ["Durabilité exceptionnelle", "Tirages limités encadrés par la loi", "Poids et présence"],
  },
  {
    name: "Résine",
    slug: "resine",
    badge: "Moulage",
    text: "Matière synthétique polyvalente, la résine permet des tirages plus accessibles, des couleurs vives intégrées dans la masse et des formats audacieux à poids réduit. Elle est devenue le matériau emblématique de la pop sculpture contemporaine.",
    points: ["Légèreté", "Couleurs dans la masse", "Prix plus abordable que le bronze"],
  },
  {
    name: "Pierre & marbre",
    slug: "pierre",
    badge: "Taille directe",
    text: "Tailler la pierre, c'est retirer de la matière sans retour possible. Le marbre, les pierres calcaires et le granit exigent une lecture préalable du bloc : la veine, la faille, la densité guident le geste. Chaque œuvre est unique par nature.",
    points: ["Pièce unique", "Travail lent et irréversible", "Héritage des tailleurs de pierre"],
  },
  {
    name: "Bois",
    slug: "bois",
    badge: "Sculpture & assemblage",
    text: "Chêne, noyer, tilleul ou bois flotté : le bois apporte chaleur et fibre. Sculpté à la gouge, brûlé, teinté ou laissé brut, il conserve la mémoire du vivant et vieillit avec l'œuvre.",
    points: ["Matière vivante", "Textures et fibres uniques", "Sensible à l'humidité"],
  },
  {
    name: "Métal & acier",
    slug: "metal",
    badge: "Soudure & assemblage",
    text: "Acier soudé, inox poli, corten à la rouille maîtrisée : le métal domine la sculpture monumentale et l'abstraction. Il permet les porte-à-faux, les lignes tendues et les grandes envergures résistant aux intempéries.",
    points: ["Grandes dimensions possibles", "Adapté à l'extérieur", "Esthétique industrielle assumée"],
  },
  {
    name: "Plâtre",
    slug: "platre",
    badge: "Modelage",
    text: "Le matériau de l'esquisse, du moulage et des étapes de l'œuvre : fragile et d'un blanc mat lumineux, le plâtre sert le plus souvent de base à un bronze ou à une résine — et peut aussi être l'œuvre finale.",
    points: ["Matériau de l'esquisse et du moulage", "Traces du geste conservées", "Base du processus de fonte"],
    linkNote: { to: "/materiaux-et-techniques/terre", label: "Pour le modelage en argile, voir la fiche Terre & argile →" },
  },
  {
    name: "Terre & argile",
    slug: "terre",
    badge: "Modelage & cuisson",
    text: "La plus ancienne des sculptures : une motte d'argile, des mains, un feu. Modelée puis cuite, la terre devient terre cuite — le matériau des Tanagra grecques, des esquisses du Bernin et de la céramique contemporaine.",
    points: ["Le geste à même la matière", "Pièce unique ou petite série", "Du biscuit au raku"],
  },
];

const CIRE_PERDUE = [
  ["Le modèle", "Le sculpteur crée l'original en terre, en plâtre ou en cire, sur une armature."],
  ["L'empreinte", "Un moule en élastomère capture chaque détail de la surface du modèle."],
  ["La cire", "Une cire est coulée dans le moule, puis reprise et corrigée à la main par l'artiste."],
  ["Le four", "La cire est enrobée d'un matériau réfractaire, puis évacuée à la chaleur : elle se « perd »."],
  ["La coulée", "Le bronze en fusion (environ 1 150 °C) remplit le vide laissé par la cire."],
  ["La finition", "Démoulage, ciselure des défauts, patine à chaud : l'œuvre prend sa couleur définitive."],
];

export default function MateriauxPage() {
  useSEO(
    "Matériaux & techniques de la sculpture | Sculpteur Français",
    "Bronze, argile, résine, pierre, bois, métal, plâtre : les sept familles de matériaux de la sculpture, leurs techniques et la fonte à la cire perdue.",
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Matériaux et techniques de la sculpture contemporaine",
      description:
        "Bronze, argile, résine, pierre, bois, métal, plâtre : les matériaux de la sculpture et leurs techniques, dont la fonte à la cire perdue étape par étape.",
      inLanguage: "fr-FR",
      mainEntityOfPage: `${BASE}/materiaux-et-techniques`,
      author: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
      publisher: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
    },
    { type: "article" }
  );

  return (
    <ArticleShell
      testId="materiaux-page"
      eyebrow="Techniques — la matière"
      titleLines={["Matériaux & techniques", "de la sculpture"]}
      lead="Comprendre une sculpture commence par comprendre sa matière : ce qu'elle permet, ce qu'elle coûte, ce qu'elle dit. Tour d'horizon des matériaux et des gestes."
      image={{ ...IMAGES.studio, to: "/lexique-sculpture/modelage" }}
    >
      <H2>Les sept grandes familles de matériaux</H2>
      <P>
        Le choix d'un matériau n'est jamais neutre : il engage la durabilité de
        l'œuvre, son prix, son rapport à la lumière et au toucher, et souvent
        son nombre d'exemplaires.
      </P>

      <div className="mt-10 grid gap-px border border-ink/15 bg-ink/10 sm:grid-cols-2" data-testid="materiaux-grid">
        {MATERIAUX.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.05} className={i === MATERIAUX.length - 1 && MATERIAUX.length % 2 === 1 ? "bg-paper sm:col-span-2" : "bg-paper"}>
            <div className="h-full p-8 transition-colors duration-300 hover:bg-cream">
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="font-serif text-2xl text-ink">{m.name}</h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-bronze">{m.badge}</span>
              </div>
              <p className="mt-4 text-sm font-light leading-relaxed text-smoke"><RichText text={m.text} /></p>
              <ul className="mt-5 space-y-1.5 border-t border-ink/10 pt-4">
                {m.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-2 text-[13px] font-light text-body">
                    <span className="h-1 w-1 rotate-45 bg-bronze" />
                    {pt}
                  </li>
                ))}
              </ul>
              {m.linkNote && (
                <p className="mt-4 text-sm font-light">
                  <Link to={m.linkNote.to} className="link-underline text-bronze transition-colors hover:text-ink">
                    {m.linkNote.label}
                  </Link>
                </p>
              )}
              {m.slug && (
                <Link
                  to={`/materiaux-et-techniques/${m.slug}`}
                  data-testid={`materiau-card-${m.slug}`}
                  className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-bronze"
                >
                  <span className="link-underline">Lire la fiche complète</span>
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <H2>La fonte à la cire perdue, étape par étape</H2>
      <P>
        Technique vieille de plus de cinq mille ans, la cire perdue reste la
        référence pour couler un bronze fidèle au modèle de l'artiste. Son
        principe : sacrifier une cire intermédiaire pour laisser sa place au
        métal.
      </P>
      <ol className="mt-8 divide-y divide-ink/10 border-y border-ink/10" data-testid="cire-perdue-steps">
        {CIRE_PERDUE.map(([title, text], i) => (
          <li key={title} className="grid gap-2 py-5 sm:grid-cols-12 sm:gap-6">
            <span className="font-mono text-sm text-bronze sm:col-span-2">
              Étape {String(i + 1).padStart(2, "0")}
            </span>
            <span className="font-serif text-xl text-ink sm:col-span-3">{title}</span>
            <span className="text-sm font-light leading-relaxed text-smoke sm:col-span-7">{text}</span>
            {HUB_CIRE_ILLUS[i] && (
              <div className="sm:col-span-7 sm:col-start-6">
                <IlluFigure entry={HUB_CIRE_ILLUS[i]} testId={`cire-perdue-illu-${i}`} className="mt-2" />
              </div>
            )}
          </li>
        ))}
      </ol>

      <H3>Modelage, taille directe, assemblage : trois gestes fondateurs</H3>
      <P>
        Le <strong className="font-medium text-ink">modelage</strong> ajoute la matière — terre, cire, plâtre — autour
        d'une armature. La <strong className="font-medium text-ink">taille directe</strong> la retire — pierre, bois — sans
        possibilité de retour en arrière. L'<strong className="font-medium text-ink">assemblage</strong> combine des
        éléments existants — soudure de métaux, accumulation d'objets. Presque
        toute sculpture relève de l'un de ces trois gestes, ou de leur combinaison.
      </P>

      <Reveal className="mt-12 grid gap-8 sm:grid-cols-2">
        <figure>
          <Link to="/courants-et-mouvements" data-testid="materiaux-figure-link-1" className="group block">
            <div className="img-content-wrap">
              <img src={IMAGES.visitorMuseum.src} alt={IMAGES.visitorMuseum.alt} loading="lazy" className="img-content border border-ink/15" />
            </div>
          </Link>
          <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">{IMAGES.visitorMuseum.caption}<ImageCredit image={IMAGES.visitorMuseum} testid="credit-materiaux-musee" /></figcaption>
        </figure>
        <figure>
          <Link to="/lexique-sculpture/modelage" data-testid="materiaux-figure-link-2" className="group block">
            <div className="img-content-wrap">
              <img src={IMAGES.clay.src} alt={IMAGES.clay.alt} loading="lazy" className="img-content border border-ink/15" />
            </div>
          </Link>
          <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">{IMAGES.clay.caption}<ImageCredit image={IMAGES.clay} testid="credit-materiaux-terre" /></figcaption>
        </figure>
      </Reveal>

      <SeeAlso
        links={[
          { to: "/lexique-sculpture", label: "Le lexique : patine, cire perdue, EA…" },
          { to: "/guide-achat-direct-artiste", label: "Acheter une œuvre en direct" },
          { to: "/courants-et-mouvements", label: "Les courants contemporains" },
        ]}
      />
    </ArticleShell>
  );
}
